// src/lib/bondMath.js

import dayjs from "lib/dayjs";
import {
  dayCountCouponPeriodFraction,
  dayCountFraction,
  freqToPeriodsPerYear,
  isCouponPeriodDayCount,
} from "lib/dayCount";
import { computeArbYield } from "./arbYield";

// ==========================================================
// PRICE / YIELD
// ==========================================================
function buildCouponDates(maturity, frequency, settle) {
  const f = freqToPeriodsPerYear(frequency);
  const couponMonths = 12 / f;
  const out = [];
  let d = dayjs(maturity);

  for (let i = 0; i < 400; i++) {
    out.push(d);
    d = d.subtract(couponMonths, "month");
    if (d.isBefore(settle.subtract(30, "years"))) break;
  }

  return out.sort((a, b) => a.valueOf() - b.valueOf());
}

export function priceFromYield({
  couponRate,
  yieldRate,
  deliveryDate,
  maturityDate,
  datedDate = null,
  dayCount = "30/360",
  frequency = "semiannual",
  redemption = 100,
  applyParSnap = true,
}) {
  const f = freqToPeriodsPerYear(frequency);
  const c = (couponRate / f) * 100;
  const y = yieldRate / f;

  const settle = dayjs(deliveryDate);
  const maturity = dayjs(maturityDate);
  const couponDates = buildCouponDates(maturity, frequency, settle);

  let prevCoupon = null;
  let nextCoupon = null;
  for (const cd of couponDates) {
    if (cd.isAfter(settle)) {
      nextCoupon = cd;
      break;
    }
    prevCoupon = cd;
  }

  if (!nextCoupon) nextCoupon = maturity;
  if (!prevCoupon) prevCoupon = nextCoupon.subtract(12 / f, "month");

  let A;
  let E;
  let frac;
  const dc = String(dayCount || "").toLowerCase();

  const isPeriodBased = isCouponPeriodDayCount(dc);

  if (isPeriodBased) {
    A =
      dayCountCouponPeriodFraction({
        start: prevCoupon,
        end: settle,
        nextCoupon,
        method: dayCount,
        frequency,
      }) * f;
    E = 1;
    frac = 1 - A;
  } else {
    const Araw = dayCountFraction(prevCoupon, settle, dayCount);
    const Eraw = dayCountFraction(prevCoupon, nextCoupon, dayCount);
    A = Araw;
    E = Eraw === 0 ? 1 : Eraw;
    frac = (E - A) / E;
  }

  const N = couponDates.filter((cd) => cd.isAfter(settle)).length;
  const dated = datedDate ? dayjs(datedDate) : null;
  const hasShortFirstCoupon =
    dated?.isValid() &&
    dated.isAfter(prevCoupon, "day") &&
    !dated.isAfter(settle, "day") &&
    dated.isBefore(nextCoupon, "day");
  let firstCouponRatio = 1;
  let accruedRatio = A / E;

  if (hasShortFirstCoupon) {
    const fullCouponFraction = dayCountCouponPeriodFraction({
      start: prevCoupon,
      end: nextCoupon,
      nextCoupon,
      method: dayCount,
      frequency,
    });
    if (fullCouponFraction > 0) {
      firstCouponRatio =
        dayCountCouponPeriodFraction({
          start: dated,
          end: nextCoupon,
          nextCoupon,
          method: dayCount,
          frequency,
        }) / fullCouponFraction;
      accruedRatio =
        dayCountCouponPeriodFraction({
          start: dated,
          end: settle,
          nextCoupon,
          method: dayCount,
          frequency,
        }) / fullCouponFraction;
    }
  }

  let pv = 0;
  for (let k = 1; k <= N; k++) {
    const df = Math.pow(1 + y, (k - 1) + frac);
    const couponCash = c * (k === 1 ? firstCouponRatio : 1);
    const cash = k < N ? couponCash : couponCash + redemption;
    pv += cash / df;
  }

  const accrued = c * accruedRatio;
  const rawPrice = pv - accrued;

  /*
   * Market convention: par snap
   * ---------------------------
   * In municipal structuring, when coupon and yield are equal at the quoted
   * precision, usually 3 decimals as a percent, the bond is treated as a par
   * bond even when settlement does not fall on a coupon date.
   *
   * Strict fractional-period math can produce a small non-par price because
   * accrued interest and discounting mechanics do not perfectly offset when
   * settlement is between coupon dates. In practice, DBC, Bloomberg and
   * underwriting convention treat that result as a settlement artifact, not an
   * economic discount or premium.
   *
   * Therefore:
   *   coupon 5.000%, yield 5.000% => price 100.000
   *
   * This only affects the narrow par case where coupon and yield match after
   * rounding to 3 decimals as percentages. Premium and discount bonds continue
   * to use the strict computed price.
   */
  if (applyParSnap) {
    const couponPct3 = Number((Number(couponRate) * 100).toFixed(3));
    const yieldPct3 = Number((Number(yieldRate) * 100).toFixed(3));

    if (
      Number.isFinite(rawPrice) &&
      Number.isFinite(couponPct3) &&
      Number.isFinite(yieldPct3) &&
      couponPct3 === yieldPct3
    ) {
      return 100;
    }
  }

  return rawPrice;
}

export function yieldFromPrice({
  couponRate,
  priceTarget,
  deliveryDate,
  maturityDate,
  datedDate = null,
  dayCount = "30/360",
  frequency = "semiannual",
  redemption = 100,
  tol = 1e-9,
  maxIter = 200,
}) {
  const f = (y) =>
    priceFromYield({
      couponRate,
      yieldRate: y,
      deliveryDate,
      maturityDate,
      datedDate,
      dayCount,
      frequency,
      redemption,
      applyParSnap: false,
    }) - priceTarget;

  let low = -0.5;
  let high = 1.0;
  let fLow = f(low);

  for (let i = 0; i < maxIter; i++) {
    const mid = 0.5 * (low + high);
    const fm = f(mid);
    if (Math.abs(fm) < tol) return mid;

    if (fLow * fm < 0) {
      high = mid;
    } else {
      low = mid;
      fLow = fm;
    }
  }

  throw new Error("Yield solver did not converge");
}

function normalizeCouponRate(coupon) {
  const n = Number(coupon);
  if (!Number.isFinite(n)) return null;
  return n > 1.5 ? n / 100 : n;
}

function normalizeFrequency(frequency) {
  const n = Number(frequency);
  if (Number.isFinite(n) && n > 0) return n;
  return frequency || "semiannual";
}

export function yieldFromZeroPrice({
  priceTarget,
  settlementDate,
  maturityDate,
  dayCount = "act/365",
  redemption = 100,
}) {
  const price = Number(priceTarget);
  if (!Number.isFinite(price) || price <= 0) return null;

  const settle = dayjs(settlementDate);
  const maturity = dayjs(maturityDate);
  if (
    !settle.isValid() ||
    !maturity.isValid() ||
    !maturity.isAfter(settle, "day")
  ) {
    return null;
  }

  const t = dayCountFraction(settle, maturity, dayCount);
  if (!Number.isFinite(t) || t <= 0) return null;

  return Math.pow(redemption / price, 1 / t) - 1;
}

export function computePositionYields({
  purchasePrice,
  settlementDate,
  couponType,
  couponRate,
  maturityDate,
  paymentFrequency,
  dayCountBasis = "30/360",
  isCallable = false,
  nextCallDate = null,
  maturityRedemption = 100,
  callRedemption = 100,
}) {
  const priceTarget = Number(purchasePrice);
  if (!Number.isFinite(priceTarget) || priceTarget <= 0) {
    return { ytm: null, ytc: null, ytw: null };
  }

  const settle = dayjs(settlementDate);
  const maturity = dayjs(maturityDate);
  if (
    !settle.isValid() ||
    !maturity.isValid() ||
    !maturity.isAfter(settle, "day")
  ) {
    return { ytm: null, ytc: null, ytw: null };
  }

  const type = String(couponType || "").toUpperCase();

  if (type === "ADJUSTABLE") {
    return { ytm: null, ytc: null, ytw: null };
  }

  if (type === "ZERO") {
    const ytm = yieldFromZeroPrice({
      priceTarget,
      settlementDate,
      maturityDate,
      dayCount: dayCountBasis || "act/365",
      redemption: maturityRedemption ?? 100,
    });

    return { ytm, ytc: null, ytw: ytm };
  }

  const coupon = normalizeCouponRate(couponRate);
  if (coupon == null) {
    return { ytm: null, ytc: null, ytw: null };
  }

  const frequency = normalizeFrequency(paymentFrequency);

  let ytm = null;
  let ytc = null;

  try {
    ytm = yieldFromPrice({
      couponRate: coupon,
      priceTarget,
      deliveryDate: settlementDate,
      maturityDate,
      dayCount: dayCountBasis || "30/360",
      frequency,
      redemption: maturityRedemption ?? 100,
    });
  } catch {
    ytm = null;
  }

  const callDate = dayjs(nextCallDate);
  if (isCallable && callDate.isValid() && callDate.isAfter(settle, "day")) {
    try {
      ytc = yieldFromPrice({
        couponRate: coupon,
        priceTarget,
        deliveryDate: settlementDate,
        maturityDate: nextCallDate,
        dayCount: dayCountBasis || "30/360",
        frequency,
        redemption: callRedemption ?? 100,
      });
    } catch {
      ytc = null;
    }
  }

  const ytw =
    ytm == null
      ? ytc
      : ytc == null
      ? ytm
      : Math.min(ytm, ytc);

  return { ytm, ytc, ytw };
}

// ==========================================================
// METRICS / HELPERS
// ==========================================================
export function computeBondYears(schedule, datedDate, dayCount = "30/360") {
  if (!schedule?.length) return 0;
  return schedule.reduce((sum, r) => {
    const p = Number(r.principal) || 0;
    if (!p || !r.date) return sum;
    const tYears = dayCountFraction(datedDate, r.date, dayCount);
    return sum + p * tYears;
  }, 0);
}

export function computeNIC(totalInterest, bondYearDollars, discPrem = 0) {
  if (!bondYearDollars) return 0;
  return (Number(totalInterest) + Number(discPrem)) / bondYearDollars;
}

export function computePurchasePrice(rows) {
  if (!Array.isArray(rows)) return 0;
  return rows.reduce(
    (sum, r) =>
      sum + (Number(r.principal) || 0) * ((Number(r.price) || 100) / 100),
    0
  );
}

export function computeIRR(
  cashflows,
  compPerYear = 2,
  guess = 0.05,
  tol = 1e-10,
  maxIter = 100
) {
  let r = guess;
  for (let iter = 0; iter < maxIter; iter++) {
    const g = 1 + r / compPerYear;
    let npv = 0;
    let dnpv = 0;

    for (const cf of cashflows) {
      const exp = compPerYear * (Number(cf.t) || 0);
      const df = Math.pow(g, exp);
      npv += cf.amount / df;
      dnpv += -exp * cf.amount * Math.pow(g, -exp - 1) * (1 / compPerYear);
    }

    const next = r - npv / dnpv;
    if (!Number.isFinite(next)) break;
    if (Math.abs(next - r) < tol) return next;
    r = next;
  }
  return r;
}

export function computeWeightedAvgCoupon(rows) {
  if (!Array.isArray(rows) || !rows.length) return 0;
  let sumPrincipal = 0;
  let sumWeighted = 0;

  for (const r of rows) {
    const p = Number(r.principal) || 0;
    if (p <= 0) continue;

    let c = Number(r.coupon);
    if (!Number.isFinite(c)) continue;
    if (c > 1.5) c /= 100;

    sumPrincipal += p;
    sumWeighted += p * c;
  }

  return sumPrincipal ? sumWeighted / sumPrincipal : 0;
}

/**
 * Issue average coupon based on gross contractual interest per bond-year.
 * Debt-service adjustments (including accrued-interest and Cap-I funding)
 * intentionally do not affect gross interest.
 */
export function computeAverageCouponByBondYears(
  flowRows,
  datedDate,
  dayCount = "30/360"
) {
  if (!Array.isArray(flowRows) || !flowRows.length || !datedDate) return 0;

  let grossInterest = 0;
  let bondYears = 0;

  for (const row of flowRows) {
    grossInterest += Number(row?.interest) || 0;

    const principal = Number(row?.principal) || 0;
    if (principal <= 0 || !row?.date) continue;

    const years = dayCountFraction(datedDate, row.date, dayCount);
    if (Number.isFinite(years) && years > 0) {
      bondYears += principal * years;
    }
  }

  return bondYears > 0 ? grossInterest / bondYears : 0;
}

export function computeWeightedAvgPrice(rows) {
  if (!Array.isArray(rows) || !rows.length) return 0;
  let sumPrincipal = 0;
  let sumWeighted = 0;

  for (const r of rows) {
    const p = Number(r.principal) || 0;
    const pr = Number(r.price);
    if (p <= 0 || !Number.isFinite(pr) || pr <= 0) continue;

    sumPrincipal += p;
    sumWeighted += p * pr;
  }

  return sumPrincipal ? sumWeighted / sumPrincipal : 0;
}

export function computeWAL(schedule = [], datedDate, dayCount = "30/360") {
  if (!Array.isArray(schedule) || schedule.length === 0) return 0;
  if (!datedDate) return 0;

  const start = dayjs(datedDate);
  if (!start.isValid()) return 0;

  const totalPrincipal = schedule.reduce(
    (sum, r) => sum + (Number(r.principal) || 0),
    0
  );
  if (totalPrincipal <= 0) return 0;

  let numerator = 0;

  for (const r of schedule) {
    const p = Number(r.principal) || 0;
    const date = dayjs(r.date);
    if (p <= 0 || !date.isValid()) continue;

    const tYears = dayCountFraction(start, date, dayCount);
    numerator += p * tYears;
  }

  return numerator / totalPrincipal;
}

export function computeWAM(
  allocations = [],
  settlementDate,
  dayCountBasis = "act/365"
) {
  if (!Array.isArray(allocations) || allocations.length === 0) return 0;
  if (!settlementDate) return 0;

  const anchor = dayjs(settlementDate);
  if (!anchor.isValid()) return 0;

  const totalMV = allocations.reduce(
    (sum, a) => sum + (Number(a.cost) || 0),
    0
  );
  if (totalMV <= 0) return 0;

  return allocations.reduce((acc, a) => {
    const mv = Number(a.cost) || 0;
    const maturity = dayjs(a.maturityDate);

    if (mv <= 0 || !maturity.isValid()) return acc;

    const days = maturity.diff(anchor, "day");
    const years = dayCountBasis === "act/365" ? days / 365 : days / 365;

    const weight = mv / totalMV;
    return acc + weight * years;
  }, 0);
}

// ==========================================================
// SAFE ROUNDING
// ==========================================================
export function safeCoupon(c) {
  if (c == null || !Number.isFinite(Number(c))) return "";
  return Number(Number(c).toFixed(3));
}

export function safeYield(y) {
  if (y == null || !Number.isFinite(Number(y))) return "";
  const pct = y * 100;
  return Number(pct.toFixed(3));
}

export function safePrice(p) {
  if (p == null || !Number.isFinite(Number(p))) return "";
  return Number(p.toFixed(3));
}

// ==========================================================
// DURATION & DV01
// ==========================================================
export function computeModifiedDuration(
  flowRows = [],
  yieldRate,
  compPerYear = 2,
  dayCount = "30/360"
) {
  if (
    !Array.isArray(flowRows) ||
    flowRows.length === 0 ||
    !Number.isFinite(yieldRate)
  ) {
    return 0;
  }

  const startDate = flowRows[0]?.date ? flowRows[0].date : null;
  if (!startDate) return 0;

  const pvFlows = flowRows.map((r) => {
    const t = dayCountFraction(startDate, r.date, dayCount);
    const amount = Number(r.total) || 0;
    const df = Math.pow(1 + yieldRate / compPerYear, t * compPerYear);
    const pv = amount / df;
    return { t, pv };
  });

  const pvSum = pvFlows.reduce((s, f) => s + f.pv, 0);
  if (!pvSum) return 0;

  const weightedTime = pvFlows.reduce((s, f) => s + f.t * f.pv, 0);
  const macaulay = weightedTime / pvSum;
  return macaulay / (1 + yieldRate / compPerYear);
}

export function computeDV01FromDuration({
  flowRows = [],
  yieldRate,
  compPerYear = 2,
  purchasePrice,
  totalPrincipal,
}) {
  if (
    !Array.isArray(flowRows) ||
    !flowRows.length ||
    !Number.isFinite(yieldRate)
  ) {
    return 0;
  }

  const modDur = computeModifiedDuration(flowRows, yieldRate, compPerYear);
  const marketValue =
    (Number(totalPrincipal) || 0) * ((Number(purchasePrice) || 100) / 100);

  return modDur * marketValue * 0.0001;
}

// ==========================================================
// FUNDPLANNER HELPERS
// ==========================================================
/**
 * computePortfolioDV01
 * ----------------------
 * Computes approximate DV01 for a set of bullet investments (FundPlanner allocations).
 * Expects each allocation to have: principal, ratePct (annual %), price, maturityDate.
 */
export function computePortfolioDV01(
  allocations = [],
  {
    compPerYear = 2,
    anchorDate,
    dayCountBasis = "act/365.25",
  } = {}
) {
  if (!Array.isArray(allocations) || allocations.length === 0) return 0;

  const anchor = anchorDate ? dayjs(anchorDate) : dayjs();
  if (!anchor.isValid()) return 0;

  const yearDivisor = dayCountBasis === "act/365" ? 365 : 365.25;

  let totalDV01 = 0;

  for (const a of allocations) {
    const principal = Number(a.principal) || 0;
    if (principal <= 0) continue;

    const price = Number.isFinite(Number(a.price)) ? Number(a.price) : 100;
    const y = (Number(a.ratePct) || 0) / 100;

    const maturity = dayjs(a.maturityDate);
    if (!maturity.isValid()) continue;

    const days = maturity.diff(anchor, "day");
    if (days <= 0) continue;

    const years = days / yearDivisor;
    const dmod = years / (1 + y / compPerYear);
    const marketValue = principal * (price / 100);
    const dv01 = marketValue * dmod * 0.0001;

    totalDV01 += dv01;
  }

  return totalDV01;
}

// ============================================================================
// IRS ARBITRAGE YIELD
// ============================================================================
export { computeArbYield };
